import type { PortalListResult, PortalPaymentSummary } from '@erp/shared';
import { Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Card, DataTable, type DataTableColumn, EmptyState, Skeleton } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { formatMoney } from '../../lib/money.js';

/**
 * The payments table, header-less — a TAB of the merged statement page (see
 * PortalInvoicesPanel). The old /portal/payments route redirects in.
 */
export function PortalPaymentsPanel(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const [data, setData] = useState<PortalListResult<PortalPaymentSummary> | null>(null);

  useEffect(() => {
    void customerApi()
      .get<PortalListResult<PortalPaymentSummary>>('/payments', { params: { limit: 200 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }));
  }, []);

  const columns: DataTableColumn<PortalPaymentSummary>[] = useMemo(
    () => [
      {
        id: 'number',
        header: t('columns.number'),
        sortValue: (r) => r.docNumber,
        searchValue: (r) => `${r.docNumber} ${r.reference ?? ''}`,
        cell: (r) => (
          <Link to={`/portal/payments/${r.id}`} className="font-mono text-xs text-brand hover:underline">
            {r.docNumber}
          </Link>
        ),
      },
      { id: 'date', header: t('columns.date'), sortValue: (r) => r.paymentDate, cell: (r) => r.paymentDate },
      { id: 'method', header: t('columns.method'), sortValue: (r) => r.method ?? '', cell: (r) => r.method ?? '—' },
      {
        id: 'amount',
        header: t('columns.amount'),
        align: 'end',
        cellClassName: 'font-mono tabular-nums',
        sortValue: (r) => Number(r.amount),
        cell: (r) => formatMoney(r.amount, { locale: i18n.language, currency: r.currencyCode }),
      },
    ],
    [t, i18n.language],
  );

  return !data ? (
    <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
  ) : data.items.length === 0 ? (
    <EmptyState icon={<Wallet className="h-8 w-8" aria-hidden />} title={t('payments.empty')} description={t('payments.emptyDescription')} />
  ) : (
    <Card className="overflow-hidden p-0">
      <DataTable columns={columns} data={data.items} rowKey={(r) => r.id} searchPlaceholder={t('payments.searchPlaceholder')} />
    </Card>
  );
}
